import { useEffect, useState } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Input, Switch } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import { useSelector } from "react-redux";
import { getCountryNameByCode } from "../../../../utils/countryDictionary";
import "../../../../assets/css/admin/countries and offices/EditEntityModal.css";

const fieldSpacing = { marginBottom: 16 };

const warningBoxStyle = {
    border: "1px dashed #3bb3d6",
    background: "#f7f7f7",
    padding: 12,
    borderRadius: 8,
    color: "#555",
    fontSize: 13,
    lineHeight: 1.4,
    marginTop: 10,
};

const getCountryText = (country) => {
    if (!country) return "";
    return country.name || country.countryName || country.country || "";
};

const getCityText = (city) => {
    if (!city) return "";
    return city.cityName || city.name || "";
};

const findCountry = (countryList, data) => {
    if (!Array.isArray(countryList) || countryList.length === 0 || !data) return null;

    const byId = countryList.find((item) => {
        const currentId = item.id ?? item.countryId;
        return currentId && (currentId === data.countryId || currentId === data.country?.id);
    });
    if (byId) return byId;

    const expectedName = data.countryName || data.country || data.country?.name;
    if (!expectedName) return null;

    return countryList.find((item) => getCountryText(item) === expectedName) || null;
};

const findCity = (cityList, data) => {
    if (!Array.isArray(cityList) || cityList.length === 0 || !data) return null;

    const byId = cityList.find((item) => {
        const currentId = item.id ?? item.cityId;
        return currentId && (currentId === data.cityId || currentId === data.city?.id);
    });
    if (byId) return byId;

    const expectedName = data.cityName || data.city || data.city?.name;
    if (!expectedName) return null;

    return cityList.find((item) => getCityText(item) === expectedName) || null;
};

const findCountryByUser = (countryList, user) => {
    if (!Array.isArray(countryList) || countryList.length === 0 || !user) return null;

    const userCountryId = user.countryId ?? user.country?.id ?? null;
    if (userCountryId !== null && userCountryId !== undefined) {
        const byId = countryList.find((item) => {
            const currentId = item.id ?? item.countryId;
            return currentId && String(currentId) === String(userCountryId);
        });

        if (byId) return byId;
    }

    const userCountryCode = String(user.country || "").trim().toLowerCase();
    if (userCountryCode) {
        const mappedCountryName = getCountryNameByCode(userCountryCode).toLowerCase();
        const byCode = countryList.find((item) => {
            const candidates = [
                item.id,
                item.countryId,
                item.code,
                item.countryCode,
                item.isoCode,
                item.alpha2Code,
                item.shortName,
                item.abbreviation,
                item.country,
                item.countryName,
                item.name,
            ]
                .filter(Boolean)
                .map((value) => String(value).trim().toLowerCase());

            return candidates.includes(userCountryCode) || (mappedCountryName && candidates.includes(mappedCountryName));
        });

        if (byCode) return byCode;
    }

    return null;
};

const findCityByUser = (cityList, user) => {
    if (!Array.isArray(cityList) || cityList.length === 0 || !user) return null;

    if (user.cityId === null || user.cityId === undefined) return null;

    const byId = cityList.find((item) => {
        const currentId = item.id ?? item.cityId;
        const userCityId = localStorage.getItem("city") ?? user.cityId;
        return currentId && String(currentId) === String(userCityId);
    });

    return byId || null;
};

const getEntityId = (entity) => entity?.id ?? entity?.entityId ?? null;

const getEntityName = (entity) => entity?.entityName || entity?.name || "";

const getCountryDisplayText = (country, user) => {
    if (country) return getCountryText(country);
    const mappedCountryName = getCountryNameByCode(user?.country);
    if (mappedCountryName) return mappedCountryName;

    return String(user?.country || "").toUpperCase();
};

const getCityDisplayText = (city, user) => {
    if (city) return getCityText(city);
    return user?.cityName || "";
};

const mockCheckEntityNameExists = async ({ entityName, currentEntityId, entities }) => {
    const normalizedName = entityName.trim().toLowerCase();
    if (!normalizedName) return false;

    // TODO: Replace this mock with the API call once endpoint is available.
    return (entities || []).some((entity) => {
        const currentName = getEntityName(entity).trim().toLowerCase();
        const entityId = getEntityId(entity);
        return currentName === normalizedName && entityId !== currentEntityId;
    });
};

const EditEntityModal = ({
    open,
    onClose,
    onSave,
    entityData,
    countries,
    cities,
    existingEntities = [],
    mode = "edit",
}) => {
    const isEditMode = mode === "edit";
    const dialogTitle = isEditMode ? "Edit entity" : "Customize the new entity";

    const auth = useSelector((state) => {
        return state.auth;
    });
    const { user } = auth || {};

    const [entityName, setEntityName] = useState("");
    const [country, setCountry] = useState(null);
    const [city, setCity] = useState(null);
    const [status, setStatus] = useState(true);
    const [showDisableWarning, setShowDisableWarning] = useState(false);
    const [entityNameExists, setEntityNameExists] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!open) return;

        const userCountry = findCountryByUser(countries, user);
        const userCity = findCityByUser(cities, user);
        const initialCountry = userCountry || findCountry(countries, entityData);
        const initialCity = userCity || findCity(cities, entityData);
        const initialStatus =
            typeof entityData?.status === "boolean"
                ? entityData.status
                : typeof entityData?.isActive === "boolean"
                    ? entityData.isActive
                    : true;

        setEntityName(entityData?.entityName || "");
        setCountry(initialCountry);
        setCity(initialCity);
        setStatus(isEditMode ? initialStatus : true);
        setShowDisableWarning(isEditMode ? !initialStatus : false);
        setEntityNameExists(false);
        setErrors({});
    }, [open, entityData, countries, cities, isEditMode, user]);

    const handleStatusChange = (e) => {
        const nextStatus = e.value;
        setStatus(nextStatus);
        setShowDisableWarning(!nextStatus);
    };

    const validateEntityNameOnBlur = async () => {
        const trimmedName = entityName.trim();
        if (!trimmedName) {
            setEntityNameExists(false);
            return false;
        }

        const exists = await mockCheckEntityNameExists({
            entityName: trimmedName,
            currentEntityId: getEntityId(entityData),
            entities: existingEntities,
        });

        setEntityNameExists(exists);
        return exists;
    };

    const validate = async () => {
        const nextErrors = {};
        const duplicateNameFound = await validateEntityNameOnBlur();

        if (!entityName.trim()) {
            nextErrors.entityName = true;
        }

        if (duplicateNameFound) {
            nextErrors.entityName = true;
        }

        if (!city) {
            nextErrors.city = true;
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSave = async () => {
        if (!await validate()) return;

        onSave?.({
            entityName: entityName.trim(),
            country,
            city,
            status,
            showDisableWarning: isEditMode ? showDisableWarning : false,
        });
    };

    if (!open) return null;

    const isEntityNameRequired = !!errors.entityName && !entityName.trim();
    const showDuplicateNameError = entityNameExists && !isEntityNameRequired;

    return (
        <Dialog title={dialogTitle} onClose={onClose} width={400} className="edit-entity-modal" closeIcon={false}>
            <div style={{ display: "flex", flexDirection: "column", backgroundColor: "white", padding: 20, borderRadius: 8 }}>
                <div style={fieldSpacing}>
                    <Label editorId="entityNameInput" className="entity-label">*Entity name</Label>
                    <Input
                        id="entityNameInput"
                        className="entity-text-input"
                        value={entityName}
                        onChange={(e) => {
                            setEntityName(e.value || "");
                            setEntityNameExists(false);
                            setErrors((prev) => ({ ...prev, entityName: false }));
                        }}
                        onBlur={validateEntityNameOnBlur}
                        style={errors.entityName || entityNameExists ? { borderColor: "#d9534f" } : undefined}
                    />
                    {isEntityNameRequired && (
                        <div className="entity-name-required-error">Entity name is required.</div>
                    )}
                    {showDuplicateNameError && (
                        <div className="entity-name-exists-error">This entity name already exists.</div>
                    )}
                </div>

                <div style={fieldSpacing}>
                    <Label editorId="countryDropdown" className="entity-label">Country</Label>
                    <Input
                        id="countryDropdown"
                        disabled={true}
                        className="entity-text-input"
                        value={getCountryDisplayText(country, user)}
                        readOnly={true}
                    />
                </div>

                <div style={fieldSpacing}>
                    <Label editorId="cityDropdown" className="entity-label">*City</Label>
                    <Input
                        id="cityDropdown"
                        className="entity-text-input"
                        value={getCityDisplayText(city, user)}
                        disabled={true}
                        readOnly={true}
                        style={errors.city ? { borderColor: "#d9534f" } : undefined}
                    />
                </div>

                {/* {isEditMode && ( */}
                <div style={fieldSpacing}>
                    <Label editorId="entityStatus" className="entity-label">Status</Label>
                    <div className="status-container">
                        <Switch id="entityStatus" checked={status} onChange={handleStatusChange} />
                        <span className="entity-status-value" style={{ fontWeight: 600 }}>{status ? "ON" : "OFF"}</span>
                    </div>

                    {showDisableWarning && (
                        <div className="entity-warning-box" style={warningBoxStyle}>
                            Disabling this entity will remove it from all associated areas. This action may affect the current configurations.
                        </div>
                    )}
                </div>
                {/* )} */}

                <div className="modal-footer">
                    <Button className="entity-cancel-btn" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button className="entity-save-btn" primary={true} onClick={handleSave}>
                        Save
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

export default EditEntityModal;
